import {isInvalidArgumentError, isNotFoundError} from '@aboutyou/data-fetcher';
import {
  Element,
  Element_Link,
  Page,
  Page_SeoData,
  Page_Unavailable,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/page.types';
import {createNotFoundHint} from '@aboutyou/router';
import {capitalize, exists} from '@aboutyou/utils';

export const isSeoData = (
  page: Page | undefined,
): page is {type: {$case: 'seoData'; seoData: Page_SeoData}} => {
  return Boolean(page && page.type && page.type.$case === 'seoData');
};

export const isUnavailable = (
  page: Page | undefined,
): page is {type: {$case: 'unavailable'; unavailable: Page_Unavailable}} => {
  return Boolean(page?.type?.$case === 'unavailable');
};

export type MetaTag = {name?: string; property?: string; content: string};

export type LinkTag = Element_Link & {hrefLang?: string};

export type MetaTagWithDataTestid = MetaTag & {'data-testid'?: string};

export type LinkWithDataTestid = LinkTag & {'data-testid'?: string};

export type SeoMeta = Page | undefined;

export const extractMetaAndLinkTags = (
  elements: Element[],
): [MetaTag[], LinkTag[]] => {
  const metaTags = [];
  const linkTags = [];

  for (const element of elements) {
    if (element.type?.$case === 'meta') {
      const metaTag: MetaTag = {
        content: element.type.meta.content,
      };

      if (element.type.meta.type?.$case === 'name') {
        metaTag.name = element.type.meta.type.name;
      }
      if (element.type.meta.type?.$case === 'property') {
        metaTag.property = element.type.meta.type.property;
      }

      metaTags.push(metaTag);
    }

    if (element.type?.$case === 'link') {
      const linkTag: LinkTag = {
        ...element.type.link,
      };

      if (exists(linkTag.hreflang)) {
        linkTag.hrefLang = linkTag.hreflang.replace('-ZZ', '');
        delete linkTag.hreflang;
      }

      linkTags.push(linkTag);
    }
  }

  return [metaTags, linkTags];
};

export const setDataTestIds = (
  elements: [MetaTag[], LinkTag[]],
): [MetaTagWithDataTestid[], LinkWithDataTestid[]] => {
  const getMetaDataTestid = (metaTag: MetaTag) => {
    return `Meta-${'name' in metaTag ? metaTag.name : metaTag.property}`;
  };

  const getLinkDataTestid = (linkTag: LinkTag) => {
    if (exists(linkTag.media) && linkTag.rel === 'alternate') {
      return 'MobileAlternate';
    }

    if (exists(linkTag.hrefLang)) {
      return 'Hreflang';
    }

    return `Link${capitalize(linkTag.rel)}`;
  };

  const [metaTags, linkTags] = elements;

  const metaTagsWithDataTestid = metaTags.map(metaTag => {
    return {
      ...metaTag,
      'data-testid': getMetaDataTestid(metaTag),
    };
  });

  const linkWithDataTestid = linkTags.map(linkTag => {
    return {
      ...linkTag,
      'data-testid': getLinkDataTestid(linkTag),
    };
  });

  return [metaTagsWithDataTestid, linkWithDataTestid];
};

export const ensure404Page = (error: unknown) => {
  if (isNotFoundError(error) || isInvalidArgumentError(error)) {
    throw createNotFoundHint();
  }
};
